/**
 * Convert Tripetto @slotId references to Formly {{fieldKey}} references
 * V2: Uses NodeToFieldMap with actual Formly field data
 */

import { NodeToFieldMap } from './node-field-map';
import { convertSingleCondition, findOptionValue, TripettoCondition } from './single-condition-converter';
import { formatValue } from './legacy-condition-helpers';

/**
 * Convert an array of Tripetto conditions into a hideExpression string
 * @param conditions Array of conditions to convert
 * @param nodeFieldMap Map containing actual Formly field data
 * @param culling Tripetto culling mode: undefined/null = OR (default), "all" = AND, "none" = NOT, "each" = special case
 * Returns undefined if conditions cannot be converted
 */
export function convertBranchConditionsToHideExpression(
	conditions: TripettoCondition[],
	nodeFieldMap: NodeToFieldMap,
	culling?: string
): string | undefined {
	if (!conditions || conditions.length === 0) {
		return undefined;
	}

	// Handle "each" - cannot be converted to Formly
	if (culling === 'each') {
		return undefined;
	}

	// Check if all conditions check the same field for equality with different values
	// If so, use array.includes() syntax for OR logic
	const fieldChecks = new Map<string, { values: any[]; isEquals: boolean }>();

	for (const condition of conditions) {
		const block = condition.block;
		if (!block) continue;

		const targetSlotId = block.slot;
		if (!targetSlotId) continue;

		const fieldKey = nodeFieldMap.getFieldKeyBySlotId(targetSlotId);
		if (!fieldKey) continue;

		// Determine if this is an equals check
		let isEqualsCheck = false;
		let matchValue: any;

		if (block.type === 'checkboxes-condition2' && block.checkbox) {
			matchValue = block.checkbox.value || block.checkbox.name;
			isEqualsCheck = block.checked !== false;
		} else if (
			(block.type === 'radiobuttons-condition2' || block.type === 'radiobuttons-condition3') &&
			block.button
		) {
			matchValue = block.button.value || block.button.name;
			isEqualsCheck = true;
		} else if (block.type && block.type.includes('condition')) {
			const mode = block.mode;
			matchValue = block.match ?? block.value;
			isEqualsCheck = mode === 'is-matched' || mode === 'isMatched' || mode === 'match';
		}

		if (isEqualsCheck && matchValue !== undefined) {
			if (!fieldChecks.has(fieldKey)) {
				fieldChecks.set(fieldKey, { values: [], isEquals: true });
			}
			const fieldCheck = fieldChecks.get(fieldKey)!;
			fieldCheck.values.push(matchValue);
		}
	}

	// If all conditions check the same field for equality with different values, use includes()
	// This only makes sense for OR (default) culling mode
	if (!culling && fieldChecks.size === 1) {
		const [fieldKey, { values, isEquals }] = Array.from(fieldChecks.entries())[0];
		if (isEquals && values.length === conditions.length && values.length > 1) {
			// Multiple equality checks on same field - use array.includes()
			const valuesArray = values.map((v) => formatValue(v)).join(',');
			return `![${valuesArray}].includes(model['${fieldKey}'])`;
		}
	}

	// Otherwise, convert each condition individually and combine based on culling mode
	const expressions: string[] = [];

	for (const condition of conditions) {
		const expr = convertSingleCondition(condition, nodeFieldMap);
		if (!expr) {
			// If any condition can't be converted, return undefined
			// This will cause the field to be imported with a warning instead
			return undefined;
		}
		expressions.push(expr);
	}

	if (expressions.length === 0) {
		return undefined;
	}

	// Combine conditions based on culling mode
	if (expressions.length === 1) {
		// Single condition - same logic for all culling modes
		if (culling === 'none') {
			// NOT: Hide when condition IS true (show when NOT true)
			return expressions[0];
		} else {
			// OR/AND: Hide when condition is NOT true
			return `!(${expressions[0]})`;
		}
	}

	// Multiple conditions - combine based on culling mode
	if (culling === 'all') {
		// AND: Show when ALL conditions are true
		// Hide when NOT (all conditions are true)
		return `!(${expressions.join(' && ')})`;
	} else if (culling === 'none') {
		// NOT/NOR: Show when NONE of the conditions are true
		// Hide when ANY condition is true
		return expressions.join(' || ');
	} else {
		// Default OR: Show when ANY condition is true
		// Hide when NOT (any condition is true) = Hide when all are false
		return `!(${expressions.join(' || ')})`;
	}
}

// Re-export for convenience
export { findOptionValue, TripettoCondition };
